import { CustomHeader, Screen, Text } from "components"
import React, { useCallback, useEffect } from "react"
import { FlatList, Image, Linking, TouchableOpacity, View } from "react-native"
import { style } from "./styles"
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import { colors } from "theme"
import { translate } from "i18n"
import Icon from "@expo/vector-icons/MaterialCommunityIcons"
import { useStores } from "models"
import flash from "config/flash"
import { observer } from "mobx-react-lite"
import { useColor } from "app/theme/getThemes"
import { version } from "../../../../package.json"
import moment from "moment"

export const ProfileScreen = observer(() => {
  const { userStore, couponStore } = useStores()
  const { companyTheme } = useColor()
  const navigation = useNavigation<any>()

  const data = [
    {
      logoName: "card-account-details-outline",
      displayName: translate("editProfileScreen.title"),
      navigateTo: "editProfile",
    },
    {
      logoName: "wallet-giftcard",
      displayName: translate("pointHistoryScreen.title"),
      navigateTo: "pointHistory",
    },
    {
      logoName: "onepassword",
      displayName: "Change 6-Digit PIN",
      navigateTo: "editPin",
    },
    {
      logoName: "ticket-confirmation-outline",
      displayName: "My Rewards",
      navigateTo: "voucher",
    },
    {
      logoName: "bell-outline",
      displayName: "Notification",
      navigateTo: "notification",
    },
    {
      logoName: "bookmark-outline",
      displayName: "Feedback",
      navigateTo: "feedback",
    },
    {
      logoName: "share-all",
      displayName: "Referral",
      navigateTo: "referral",
    },
    {
      logoName: "account-cancel",
      displayName: "Delete Account",
      navigateTo: "deleteAccount",
    },
    {
      logoName: "gavel",
      displayName: "T&C",
      navigateTo: {
        navigator: "auth",
        screen: "webView",
        params: { name: "Tnc" },
      },
    },
    {
      logoName: "lock-outline",
      displayName: "Privacy Policy",
      navigateTo: {
        navigator: "auth",
        screen: "webView",
        params: { name: "PrivacyPolicy" },
      },
    },
    {
      logoName: "logout",
      displayName: translate("profileScreen.logout"),
      navigateTo: "auth",
    },
  ]

  const onPressButton = (screen) => {
    if (screen === "auth") {
      navigation.reset({ index: 0, routes: [{ name: "auth" }] })
      couponStore?.clearCoupon()
      userStore?.resetAuthData()
      flash("success", translate("common.logOutSuccess"))
    } else if (screen === "deleteAccount") {
      Linking.openURL(
        "https://docs.google.com/forms/d/e/1FAIpQLScfsR10WoxeMPR60j70DvzJ5TLug4EDpmeZyPqUbBchaAdoXQ/viewform",
      )
    } else if (typeof screen === "object") {
      if (screen.navigator) {
        navigation.navigate(screen.navigator, {
          screen: screen.screen,
          params: screen.params,
        })
      } else {
        navigation.navigate(screen.screen, screen.params)
      }
    } else {
      navigation.navigate(screen)
    }
  }

  useEffect(() => {
    navigation.setOptions({
      headerShown: false,
    })
  }, [])

  useFocusEffect(
    useCallback(() => {
      getCoupon()
    }, []),
  )

  const isVoucherNew = (voucherStartDate) => {
    if (!voucherStartDate) {
      return false
    }
    try {
      const nowInKL = moment.tz("Asia/Kuala_Lumpur")
      const startDate = moment(voucherStartDate)
      const sevenDaysFromStart = startDate.clone().add(7, "days")
      return nowInKL.isBetween(startDate, sevenDaysFromStart, null, "[]")
    } catch (error) {
      console.error("Error with moment parsing:", error)
      return false
    }
  }

  const getCoupon = async () => {
    try {
      const res = await fetch(`${process.env.API_URL}/api/voucher/coupon/${userStore?.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userStore?.accessToken}`,
        },
      })

      if (res.ok) {
        const response = await res.json()
        const coupons = Array.isArray(response) ? response : [response]
        const firstVoucherForToday = coupons.find((coupon) =>
          moment(coupon.start_date).isSame(moment(), "day"),
        )

        const shouldShowNewBadge = isVoucherNew(firstVoucherForToday?.start_date)

        if (firstVoucherForToday) {
          const areIdsDifferent =
            firstVoucherForToday.voucher_catalog_id !== couponStore.newCouponNotifiedId

          if (areIdsDifferent && shouldShowNewBadge) {
            couponStore.setShowBadge(true)
            couponStore.setNewCouponNotifiedId(firstVoucherForToday.voucher_catalog_id)
          }
        }
      } else {
        const errorMsg = await res.json()
        console.log(errorMsg)
      }
    } catch (e) {
      flash("error", translate("errors.failedToUseVoucher"))
      console.error(e)
    }
  }
  return (
    <>
      <CustomHeader withBalls headerTitle={"My Profile"} longHeader showBackButton={false} />
      <Screen>
        <View style={style.bottomContainer}>
          <View style={style.overHeadContainer}>
            <>
              <View style={style.overHeadButton}>
                {userStore?.profileImage ? (
                  <Image
                    source={{ uri: userStore?.profileImage }}
                    style={style.profilePictureImage}
                    resizeMode="cover"
                  />
                ) : (
                  <>
                    <Text style={style.headerSingleUsername}>{userStore?.fullName?.charAt(0)}</Text>
                  </>
                )}
              </View>
            </>
          </View>
          <View style={style.bottomContentContainer}>
            <FlatList
              showsVerticalScrollIndicator={false}
              ListFooterComponent={() => {
                return (
                  <View style={{ justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ color: "gray" }}>Version {version}</Text>
                  </View>
                )
              }}
              style={style.flatlistContainer}
              ItemSeparatorComponent={() => {
                return <View style={style.separator} />
              }}
              data={data}
              renderItem={({ item }) => {
                const { displayName, navigateTo, logoName } = item
                return (
                  <TouchableOpacity onPress={() => onPressButton(navigateTo)} style={style.button}>
                    <View style={style.buttonTextContainer}>
                      <View
                        style={[style.buttonIconContainer, { backgroundColor: companyTheme.first }]}
                      >
                        <Icon name={logoName} size={20} color={colors.buttonTitle} />
                      </View>
                      <Text
                        style={[style.buttonText, navigateTo === "auth" && { color: colors.error }]}
                      >
                        {displayName}
                      </Text>
                    </View>
                    <Icon name={"chevron-right"} size={20} color={colors.lightGrey} />
                  </TouchableOpacity>
                )
              }}
            />
          </View>
        </View>
      </Screen>
    </>
  )
})
